import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ILink, NcgLocationMapCategory, NgcApiLocation } from '@ncg/data';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

import { LOCATION_QUERY_CATEGORY, LOCATION_QUERY_SELECTOR } from '../../../form/location-select.component';
import { LocationService } from '../../../location/location.service';
import { SidePanelService } from '../../../side-panel/side-panel.service';

@Component({
    selector: 'ncg-map-location-info-contact',
    template: `
        <ng-container *ngIf="location">
            <div class="map-item__box opening" *ngIf="location.opening_hours?.length">
                <p class="opening__title">
                    <ng-container *ngIf="currentCategory === 'retail'">{{ 'map.opening_hours_headline' | translate }}</ng-container>
                    <ng-container *ngIf="currentCategory === 'workshop'">{{ 'map.opening_hours_headline_workshop' | translate }}</ng-container>
                    <ng-container *ngIf="currentCategory === 'spareparts'">{{ 'map.opening_hours_headline_spareparts' | translate }}</ng-container>
                    <ng-container *ngIf="currentCategory === 'testdriveonly'">{{
                        'map.opening_hours_headline_testdriveonly' | translate
                    }}</ng-container>
                </p>
                <div class="columns is-gapless is-mobile" *ngFor="let time of location.opening_hours">
                    <div class="opening__day column is-5">{{ time.open_day_label }}</div>
                    <div class="opening__hour column is">
                        <ng-container *ngIf="time.open_time">{{ time.open_time }}</ng-container>
                        <ng-container *ngIf="time.open_time && time.close_time"> - </ng-container>
                        <ng-container *ngIf="time.close_time">{{ time.close_time }}</ng-container>
                    </div>
                </div>
                <div class="map-opening-hours-link" *ngIf="openingHoursLink">
                    <a ncgInterceptLinks [href]="openingHoursLink.url" [attr.title]="openingHoursLink.name" [attr.target]="openingHoursLink.target">
                        {{ openingHoursLink.name }}
                    </a>
                </div>
            </div>
            <!--Workshop-->
            <div class="map-item__box buttons-container" *ngIf="location.category?.code === workshopCode">
                <ng-container *ngIf="!hideContactButton && contactLink">
                    <ng-container *ngIf="contactLink?.isExternal; else internalContactLink">
                        <a
                            [href]="contactLink.url"
                            [attr.target]="contactLink?.target"
                            [attr.title]="contactLink.name"
                            rel="noopener"
                            class="button is-primary is-full map-link map-link--list-view">
                            {{ 'map.contact_workshop' | translate }}
                        </a>
                    </ng-container>
                    <ng-template #internalContactLink>
                        <ng-container *ngTemplateOutlet="contactButtonTmpl; context: { label: 'map.contact_workshop' | translate }"></ng-container>
                    </ng-template>
                </ng-container>
                <ng-container *ngIf="!hideServiceButton && serviceLink && location.onlineWorkshopBooking">
                    <ng-container *ngIf="serviceLink.isExternal; else internalServiceLink">
                        <a
                            [href]="serviceLink.url.replace('{0}', location.store_code ?? '')"
                            [attr.target]="serviceLink.target"
                            [attr.title]="serviceLink.name"
                            rel="noopener"
                            class="button is-primary is-full map-link map-link--list-view">
                            {{ 'map.book_workshop' | translate }}
                        </a>
                    </ng-container>
                    <ng-template #internalServiceLink>
                        <a
                            [attr.title]="serviceLink.name"
                            [routerLink]="serviceLink.url"
                            [queryParams]="storeCodeParam"
                            class="button is-primary is-full map-link map-link--list-view">
                            {{ 'map.book_workshop' | translate }}
                        </a>
                    </ng-template>
                </ng-container>
                <a
                    *ngIf="!!location.external_online_servicebooking"
                    [href]="location.external_online_servicebooking"
                    target="_blank"
                    class="button is-primary is-full map-link">
                    {{ 'map.book_service' | translate }}
                </a>
            </div>
            <!--Retail/Showroom/Spareparts-->
            <div class="map-item__box buttons-container" *ngIf="location.category?.code === retailCode || location.category?.code === sparePartsCode">
                <ng-container *ngIf="!hideContactButton && contactLink">
                    <ng-container *ngIf="contactLink?.isExternal; else internalRetailLink">
                        <a
                            [href]="contactLink.url"
                            [attr.target]="contactLink?.target"
                            [attr.title]="contactLink.name"
                            rel="noopener"
                            class="button is-primary is-full map-link map-link--list-view">
                            {{ 'map.contact' | translate }}
                        </a>
                    </ng-container>
                    <ng-template #internalRetailLink>
                        <ng-container *ngIf="isBrandLayout">
                            <ng-container
                                *ngTemplateOutlet="contactButtonTmpl; context: { label: 'map.details_page_label' | translate }"></ng-container>
                        </ng-container>
                        <ng-container *ngIf="!isBrandLayout">
                            <ng-container *ngTemplateOutlet="contactButtonTmpl; context: { label: 'map.contact' | translate }"></ng-container>
                        </ng-container>
                    </ng-template>
                </ng-container>
                <a
                    *ngIf="!!location.external_online_servicebooking"
                    [href]="location.external_online_servicebooking"
                    target="_blank"
                    class="button is-primary is-full map-link">
                    {{ 'map.book_service' | translate }}
                </a>
            </div>
            <!--Testdriveonly-->
            <div
                class="map-item__box buttons-container"
                *ngIf="!hideTestDriveButton && testDriveLink && location.category?.code === testDriveOnlyCode">
                <button
                    type="button"
                    class="button is-primary is-full map-link map-link--list-view"
                    aria-label="open to book test drive sidepanel"
                    (click)="onTestDriveClick()">
                    {{ 'map.book_test_drive' | translate }}
                </button>
            </div>
            <!--Contact button-->
            <ng-template #contactButtonTmpl let-label="label">
                <button
                    type="button"
                    class="button is-primary is-full map-link"
                    [class.is-loading]="states.isFetchingDetails"
                    [disabled]="states.isFetchingDetails"
                    aria-label="navigate to contact page"
                    (click)="onContactClick()">
                    {{ label }}
                </button>
            </ng-template>
        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MapLocationInfoContactComponent implements OnDestroy {
    @Input() location?: NgcApiLocation;
    @Input() serviceLink?: ILink;
    @Input() testDriveLink?: ILink;
    @Input() hideServiceButton?: boolean;
    @Input() hideTestDriveButton?: boolean;
    @Input() contactLink?: ILink;
    @Input() hideContactButton?: boolean;
    @Input() isBrandLayout?: boolean;
    @Input() currentCategory?: NcgLocationMapCategory;
    @Input() openingHoursLink?: ILink;
    workshopCode: NcgLocationMapCategory = 'workshop';
    retailCode: NcgLocationMapCategory = 'retail';
    sparePartsCode: NcgLocationMapCategory = 'spareparts';
    testDriveOnlyCode: NcgLocationMapCategory = 'testdriveonly';
    states = {
        isFetchingDetails: false,
    };

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private readonly locationService: LocationService,
        private readonly cd: ChangeDetectorRef,
        private readonly router: Router,
        private readonly sidePanelService: SidePanelService
    ) {}

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public onContactClick() {
        if (this.location?.store_code) {
            this.states.isFetchingDetails = true;
            this.cd.markForCheck();
            this.locationService
                .getLocationById(this.location?.store_code)
                .pipe(
                    take(1),
                    finalize(() => {
                        this.states.isFetchingDetails = false;
                        this.cd.markForCheck();
                    }),
                    takeUntil(this.unsubscribe)
                )
                .subscribe({
                    next: (data) => {
                        if (data.url) {
                            this.router.navigateByUrl(data.url).catch(() => {
                                window.location.href = data.url;
                            });
                        } else {
                            this.goToContactPage();
                        }
                    },
                    error: () => {
                        this.goToContactPage();
                    },
                });
        }
    }

    public onTestDriveClick() {
        if (this.testDriveLink) {
            this.sidePanelService.openContent(this.testDriveLink.url);
        }
    }

    public get storeCodeParam() {
        return { [LOCATION_QUERY_SELECTOR]: this.location?.store_code };
    }

    public get categoryParam() {
        return { [LOCATION_QUERY_CATEGORY]: this.location?.category?.code };
    }

    private goToContactPage() {
        if (this.contactLink?.url) {
            const link = this.contactLink.url;
            this.router
                .navigate([link], {
                    queryParams: {
                        [LOCATION_QUERY_SELECTOR]: this.location?.store_code,
                        [LOCATION_QUERY_CATEGORY]: this.location?.category?.code,
                    },
                })
                .catch(() => {
                    let query = '';
                    if (this.location) {
                        const queryArr: string[] = [];
                        if (this.location?.store_code) {
                            queryArr.push(`${LOCATION_QUERY_SELECTOR}=${this.location?.store_code}`);
                        }
                        if (this.location?.category?.code) {
                            queryArr.push(`${LOCATION_QUERY_CATEGORY}=${this.location?.category?.code}`);
                        }
                        if (queryArr.length) {
                            query = `?${queryArr.join('&')}`;
                        }
                    }
                    window.location.href = link + query;
                });
        }
    }
}
